import React, { useEffect, useId, useRef, useState } from 'react';

/**
 * DobPicker — an accessible Date-of-birth field with a calendar pop-up and an
 * easy YEAR dropdown (issue #102).
 *
 * The agent can still TYPE the date as `DD/MM/YYYY` (the underlying control is a
 * plain text input, so the screen's existing format/validation and the value it
 * submits are unchanged), OR open the calendar — which offers a month and a
 * **year `<select>`** so a birth year is one click away rather than paging month
 * by month or relying on a native control that only some browsers render with a
 * year dropdown.
 *
 * Self-contained on purpose (no shared-picker dependency) so it does not
 * hard-conflict with the sibling personal-details picker work (#100).
 *
 * Contract: `value` / `onChange` speak `DD/MM/YYYY` strings (the same shape the
 * Customer Search screen already used for its DoB text input), so the parent is
 * unchanged beyond swapping the raw `<input>` for this component.
 */

export interface DobPickerProps {
  /** Current value, `DD/MM/YYYY` (may be partial / empty while typing). */
  value: string;
  /** Called with the new `DD/MM/YYYY` string on type or calendar selection. */
  onChange: (value: string) => void;
  /** id of the visible text input (the field's <label htmlFor> points here). */
  id: string;
  /** Tailwind classes for the text input, to match the screen's other inputs. */
  inputClassName?: string;
  placeholder?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/** Parses a complete `DD/MM/YYYY` into parts, or null if not a full valid date. */
function parseDdmmyyyy(
  v: string,
): { day: number; month: number; year: number } | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v.trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  // Validate via a real Date so e.g. 31/02 is rejected rather than rolled over.
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return { day, month, year };
}

/** Formats numeric parts as `DD/MM/YYYY` (zero-padded). */
function formatDdmmyyyy(day: number, month: number, year: number): string {
  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  return `${dd}/${mm}/${year}`;
}

/** Number of days in a given (1-based) month/year. */
function daysInMonth(month: number, year: number): number {
  return new Date(year, month, 0).getDate();
}

export function DobPicker({
  value,
  onChange,
  id,
  inputClassName,
  placeholder = 'dd/mm/yyyy',
}: DobPickerProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridId = useId();

  // The calendar's view (month/year currently shown). Seeded from the typed
  // value when it is a complete date; otherwise a sensible default (a plausible
  // birth year so the agent lands near the years they actually need).
  const parsed = parseDdmmyyyy(value);
  const today = new Date();
  const defaultYear = today.getFullYear() - 30;
  const [viewMonth, setViewMonth] = useState<number>(
    parsed ? parsed.month : today.getMonth() + 1,
  );
  const [viewYear, setViewYear] = useState<number>(
    parsed ? parsed.year : defaultYear,
  );

  // When the calendar is opened, re-sync the view to the typed value (if valid).
  useEffect(() => {
    if (open && parsed) {
      setViewMonth(parsed.month);
      setViewYear(parsed.year);
    }
    // Only re-run on open toggling, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Years offered in the dropdown: 120 years back to this year (covers any
  // living customer), newest first so recent years are at the top.
  const currentYear = today.getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= currentYear - 120; y--) years.push(y);

  function selectDay(day: number): void {
    onChange(formatDdmmyyyy(day, viewMonth, viewYear));
    setOpen(false);
  }

  const totalDays = daysInMonth(viewMonth, viewYear);
  // Weekday (Mon=0) the 1st of the view month falls on, for grid offset.
  const firstWeekday = (new Date(viewYear, viewMonth - 1, 1).getDay() + 6) % 7;

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-stretch gap-2">
        <input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode="numeric"
          autoComplete="off"
          className={inputClassName}
        />
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Open calendar"
          onClick={() => setOpen((o) => !o)}
          className="px-3 border-2 border-[#dad7d3] rounded-md bg-white text-[#1d252c] hover:border-[#979c9f] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20"
        >
          <span aria-hidden="true" className="text-[18px]">
            📅
          </span>
        </button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-label="Choose date of birth"
          aria-modal="false"
          className="absolute z-[100] mt-2 w-[320px] bg-white border-2 border-[#dad7d3] rounded-md shadow-xl p-4"
        >
          <div className="flex gap-2 mb-3">
            <div className="flex flex-col gap-1 flex-1">
              <label
                htmlFor={`${gridId}-month`}
                className="text-[12px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                Month
              </label>
              <select
                id={`${gridId}-month`}
                aria-label="Month"
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                className="px-2 py-2 border-2 border-[#dad7d3] bg-white rounded-md font-['Arial',sans-serif] text-[14px] text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc]"
              >
                {MONTHS.map((name, i) => (
                  <option key={name} value={i + 1}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 w-[110px]">
              <label
                htmlFor={`${gridId}-year`}
                className="text-[12px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
              >
                Year
              </label>
              <select
                id={`${gridId}-year`}
                aria-label="Year"
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                className="px-2 py-2 border-2 border-[#dad7d3] bg-white rounded-md font-['Arial',sans-serif] text-[14px] text-[#1d252c] cursor-pointer focus:outline-none focus:border-[#005fcc]"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* A simple grid of native buttons, each with a full date as its
              accessible name. We deliberately do NOT impose ARIA grid roles:
              a proper WAI-ARIA grid needs row-owned cells + roving-tabindex
              keyboard nav, and a half-implemented grid is worse for assistive
              tech than plain, individually-focusable buttons with clear labels
              (each day is reachable by Tab and announced as a full date). */}
          <div className="grid grid-cols-7 gap-1">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                aria-hidden="true"
                className="text-center text-[11px] font-['Arial',sans-serif] font-bold text-[#6b7280] py-1"
              >
                {w}
              </div>
            ))}
            {Array.from({ length: firstWeekday }).map((_, i) => (
              <div key={`pad-${i}`} aria-hidden="true" />
            ))}
            {Array.from({ length: totalDays }).map((_, i) => {
              const day = i + 1;
              const isSelected =
                parsed != null &&
                parsed.day === day &&
                parsed.month === viewMonth &&
                parsed.year === viewYear;
              return (
                <button
                  key={day}
                  type="button"
                  aria-label={`${day} ${MONTHS[viewMonth - 1]} ${viewYear}`}
                  aria-pressed={isSelected}
                  onClick={() => selectDay(day)}
                  className={`h-9 rounded-md text-[14px] font-['Arial',sans-serif] focus:outline-none focus:ring-2 focus:ring-[#005fcc] ${
                    isSelected
                      ? 'bg-[#FF703E] text-[#1D252C] font-bold'
                      : 'text-[#1d252c] hover:bg-[#f3f4f6]'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
